import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Send, Plus, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const InviteToRfqDialog = ({ vendor, onClose }) => {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState(null);
  const [choice, setChoice] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/buyer/rfqs")
      .then((r) => {
        const open = (r.data.rfqs || []).filter((x) => x.status !== "awarded");
        setRfqs(open);
        setChoice(open[0]?.id || "new");
      })
      .catch(() => setRfqs([]));
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      let rfqId = choice;
      if (choice === "new") {
        if (!title.trim()) { setBusy(false); return toast.error("Give the RFQ a title"); }
        const { data } = await api.post("/buyer/rfqs", { title: title.trim(), items: [] });
        rfqId = data.id;
        await api.post(`/buyer/rfqs/${rfqId}/invite`, { vendor_ids: [vendor.id], emails: [] })
          .catch(() => {});
        toast.success("RFQ created — add your line items, then send it");
        onClose();
        return navigate(`/rfqs/${rfqId}`);
      }
      await api.post(`/buyer/rfqs/${rfqId}/invite`, { vendor_ids: [vendor.id], emails: [] });
      toast.success(`RFQ sent to ${vendor.company_name}`);
      onClose();
      navigate(`/rfqs/${rfqId}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the RFQ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-slate-950/60 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="invite-rfq-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-border">
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100">Send an RFQ</div>
            <div className="text-xs text-muted-foreground mt-0.5">to {vendor.company_name}</div>
          </div>
          <button data-testid="close-invite-dialog" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!rfqs ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : (
            <>
              <div>
                <Label className="mb-2 block">Add to</Label>
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {rfqs.map((r, i) => (
                    <button
                      key={r.id}
                      data-testid={`invite-pick-rfq-${i}`}
                      onClick={() => setChoice(r.id)}
                      className={`w-full text-left px-3.5 py-2.5 rounded-xl border transition-all ${choice === r.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                    >
                      <div className="text-sm font-semibold truncate">{r.title}</div>
                      <div className="text-xs text-muted-foreground">{r.item_count} items · {r.invited_count} invited</div>
                    </button>
                  ))}
                  <button
                    data-testid="invite-pick-new"
                    onClick={() => setChoice("new")}
                    className={`w-full text-left px-3.5 py-2.5 rounded-xl border transition-all ${choice === "new" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  >
                    <div className="text-sm font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New RFQ</div>
                  </button>
                </div>
              </div>

              {choice === "new" && (
                <div>
                  <Label htmlFor="quick-rfq-title">RFQ title</Label>
                  <Input id="quick-rfq-title" data-testid="quick-rfq-title-input" value={title}
                         onChange={(e) => setTitle(e.target.value)}
                         placeholder="e.g. Tower B — Glazing Package" className="mt-1.5" />
                </div>
              )}

              <Button data-testid="confirm-invite-button" onClick={send} disabled={busy} className="w-full h-11 font-semibold gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {choice === "new" ? "Create and continue" : "Send RFQ"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
